/** @format */

import {
  BreakpointsService,
  DeviceService,
  QuickviewAvailabilityService,
  WINDOW,
} from '@shared/data-access/services';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  BeaconQueryParameters,
  CompareBarProduct,
  CustomBadge,
  ProductAvailability,
} from '@shared/data-access/interfaces';
import { FallbackSeoUrlService } from '@shared/utils/services';
import {
  HmfActionTextComponent,
  HmfBadgeComponent,
  HmfButtonComponent,
  HmfPriceComponent,
  HmfStarRatingsComponent,
  HmfTextLinkComponent,
} from '@dsg-tech/homefield-angular';
import {
  HomefieldButtonModule,
  HomefieldPriceModule,
  HomefieldProductImageComponent,
  HomefieldProductImageModule,
  HomefieldStrikethroughPriceModule,
  HomefieldTextLinkModule,
} from '@ecomm-patterns-components';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconAlertInfoFill,
  homefieldIconArrowRightOutline,
  homefieldIconOrderCartFill,
  homefieldIconOrderGolfCartOutline,
  homefieldIconStarFill,
  homefieldIconStarOutline,
} from '@dsg-tech/homefield-angular-icons';
import { PICKUP_MESSAGES, SHIPPING_MESSAGES } from '@shared/data-access/constants';

import { BreakPointNew } from '@shared/data-access/enums';
import { CommonModule } from '@angular/common';
import { CompareCheckboxComponent } from './compare-checkbox/compare-checkbox.component';
import { DmnAdService } from '@shared/data-access/product-recommendations';
import { EMPTY_HMF_PRICES } from '../constants';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { ProductAvailabilityComponent } from '@shared/components';
import { ProductCardColorSwatchesComponent } from './product-card-color-swatches/product-card-color-swatches.component';
import { PromotionComponent } from './promotion/promotion.component';

@Component({
  selector: 'shared-totf-components-product-card',
  standalone: true,
  imports: [
    CommonModule,
    HomefieldButtonModule,
    HomefieldIconLibraryModule,
    HomefieldPriceModule,
    HomefieldProductImageModule,
    HomefieldStrikethroughPriceModule,
    HomefieldTextLinkModule,
    CompareCheckboxComponent,
    HmfButtonComponent,
    HmfStarRatingsComponent,
    ProductAvailabilityComponent,
    ProductCardColorSwatchesComponent,
    PromotionComponent,
    HmfActionTextComponent,
    HmfBadgeComponent,
    HmfPriceComponent,
    HmfTextLinkComponent,
  ],
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-em]': 'dataEm()',
  },
})
export class ProductCardComponent implements OnInit {
  private readonly deviceService = inject(DeviceService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly fallbackSeoUrlService = inject(FallbackSeoUrlService);
  private readonly quickviewAvailabilityService = inject(QuickviewAvailabilityService);
  private readonly window = inject(WINDOW);

  productCardImage = viewChild<HomefieldProductImageComponent>('productCardImage');

  readonly productAvailabilityData = input<ProductAvailability | null>(null);
  readonly promo = input<string | null>('');
  readonly selectedStoreName = input('');
  readonly chainAbbr = input('dsg');
  readonly referrer = input('');
  readonly seoUrl = input<string | undefined>('');
  readonly title = input('');
  readonly colors = input<string[]>([]);
  readonly ecode = input('');
  readonly sku = input('');
  readonly mapPriceIndicator = input<number | null>(null);
  readonly priceIndicator = input<number | null>(null);
  readonly prices = input(EMPTY_HMF_PRICES);
  readonly productQuickViewEnabled = input(false);
  readonly attributes = input<string | undefined>('');
  readonly imageUrl = input('');
  readonly isSponsored = input(false);
  readonly isCriteo = input(false);
  readonly ratingCount = input<number | string>(0);
  readonly showCustomerRating = input(true);
  readonly isDisabled = input(false);
  readonly isCustomOrPersonalizedProduct = input(false);
  readonly isLCPImage = input(false);
  readonly isNgOptimizedImageSupported = input(false);
  readonly isComingSoon = input(false);
  readonly isAntiClearance = input(false);
  readonly clickTrackerUrl = input('');
  readonly availabilityOnProductCardsEnabled = input(true);
  readonly categoryGroupId = input<number | string | undefined>('');
  readonly categoryIdentifier = input<string | undefined>('');
  readonly categoryType = input<string | undefined>('');
  readonly oosListEnabled = input(false);
  readonly compareCheckboxEnabled = input(false);
  readonly basketChangeBeacon = input('');
  readonly selectedForCompare = input(false);
  readonly ratingValue = input(0);
  readonly compareBarProductsCount = input(0);
  readonly sharedList = input<boolean>(false);
  readonly customBadge = input<CustomBadge | undefined>(undefined);
  readonly badge = input('', { transform: this.formatBadge.bind(this) });
  readonly isHybridPLA = input(false);
  readonly dataEm = input('');
  readonly recommendedAnalyticsData = input<{ recid: string; recToken: string }>();
  readonly displayATCIconOnXSScreens = input(true);
  readonly showSkipToComparisonBarButton = signal(false);
  readonly golfCartCheckoutIconEnabled = input(false);

  readonly moreOptionsQVEnabled = input(false);
  readonly moreOptionsPDPEnabled = input(false);
  readonly skuCount = input(0);

  readonly productCardClick = output<{ event: Event; pdpUrl: string }>();
  readonly ecodeSelected = output<string>();
  readonly compareCheckboxClick = output<{
    compareData: CompareBarProduct;
    isChecked: boolean;
  }>();

  readonly selectedSwatchColor = signal<string | undefined>(undefined);
  readonly productCardAriaLabel = computed(() => this.buildProductCardAriaLabel());

  // --- Computed signals for logic previously in ngOnInit ---
  readonly pdpUrl = computed(() => {
    let url = this.seoUrl() || this.fallbackSeoUrlService.buildSeoUrl(this.title(), this.ecode());
    if (this.referrer() && this.referrer() === 'pla') {
      url = `${url}?recid=PLA`;
    }
    if (
      this.categoryType() === 'S' ||
      (this.categoryIdentifier() && this.categoryIdentifier()?.lastIndexOf('SKU-', 0) === 0)
    ) {
      url = this.addQueryParam(url, 'categoryId', this.categoryGroupId()?.toString() ?? '');
    } else if (this.categoryIdentifier()?.toLowerCase().startsWith('clearance')) {
      url = this.addQueryParam(url, 'Clearance', 'true');
    }
    return url;
  });

  readonly updatedAssetSeoUrl = computed(() => {
    let seoUrl = '';
    if (!this.pdpUrl()) return seoUrl;

    seoUrl = this.pdpUrl();

    // Check to see if we need to add color swatch to URL
    if (this.selectedSwatchColor()) {
      seoUrl = this.addQueryParam(seoUrl, 'color', this.selectedSwatchColor() ?? '');
    }

    if (this.chainAbbr() === 'dsg' && this.isAntiClearance() === true) {
      seoUrl = this.addQueryParam(seoUrl, 'antiClearance', 'true');
    }

    // If this is a sponsored product, we need to add the clickTrackerUrl to the seoUrl
    // but we have to do it at the end so that it includes the other parameters
    if (this.isSponsored() && !this.isCriteo()) {
      seoUrl = `${this.clickTrackerUrl()}${seoUrl}`;
    }

    return seoUrl;
  });

  protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

  private readonly beaconQueryParametersClick: BeaconQueryParameters = {
    placementType: 'INGRID',
    placementLevel: 'PRODUCT',
    beaconType: 'CLICK',
  };

  private readonly beaconQueryParametersBasketChange: BeaconQueryParameters = {
    placementType: 'INGRID',
    placementLevel: 'PRODUCT',
    beaconType: 'BASKETCHANGE',
  };

  readonly isOOS = computed(
    () =>
      this.productAvailabilityData()?.onlineInventoryMessage === 'OutOfStock' &&
      this.productAvailabilityData()?.inStoreInventoryMessage === 'OutOfStock' &&
      this.oosListEnabled(),
  );

  readonly atcIcon = computed(() => {
    const showIcon =
      this.breakpointsService.breakpoint() > BreakPointNew.EXTRASMALL ||
      this.displayATCIconOnXSScreens();
    const useGolfCartIcon = this.chainAbbr() === 'gg' && this.golfCartCheckoutIconEnabled();
    const cartIcon = useGolfCartIcon ? 'order-golf-cart-outline' : 'order-cart-fill';

    return showIcon ? cartIcon : '';
  });

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconStarOutline,
      homefieldIconStarFill,
      homefieldIconAlertInfoFill,
      homefieldIconOrderCartFill,
      homefieldIconOrderGolfCartOutline,
      homefieldIconArrowRightOutline,
    ]);
  }

  readonly ratingValueDisplay = computed(() => {
    if (this.ratingValue() % 1 !== 0) {
      return Number(this.ratingValue().toFixed(1));
    }
    return this.ratingValue();
  });

  readonly shouldShowMoreOptions = computed(() => {
    if (this.colors()) {
      const hasNoColors = this.colors().length === 0;
      const hasOneEmptyColor = this.colors().length === 1 && this.colors()?.[0] === '';
      return (hasNoColors || hasOneEmptyColor) && this.skuCount() > 1;
    }

    return this.skuCount() > 1;
  });

  ngOnInit(): void {
    // Only set selectedSwatchColor if colors are present
    if (this.colors()?.length) {
      this.selectedSwatchColor.set(this.colors()[0]);
    }
  }

  handleQVClick() {
    // redirect athlete to pdp if product is customized or personalized due to quickview not supporting those products.
    if (this.isCustomOrPersonalizedProduct()) {
      this.window.location.href = this.pdpUrl();
      return;
    }

    this.quickviewAvailabilityService.queueUntilAvailable((qv) => {
      this.openQuickview(qv);
    });
  }

  private openQuickview(qv: Header.QuickView): void {
    const shouldUseCategoryGroupId =
      this.categoryType() === 'S' || this.categoryIdentifier()?.startsWith('SKU-');

    const productQuickViewParams = {
      clearance: this.categoryIdentifier()?.startsWith('Clearance') ?? false,
      skuCategory: shouldUseCategoryGroupId && this.categoryGroupId(),
      preview: '',
      skuPartNumber: this.sku(),
      defaultColor: this.selectedSwatchColor(),
      additionalData: this.recommendedAnalyticsData() ?? {},
    };

    if (this.isCriteo()) {
      this.dmnAdService.sendBeacon(
        this.basketChangeBeacon(),
        this.beaconQueryParametersBasketChange,
      );
    }
    qv.openProductQuickView(this.ecode(), this.referrer(), productQuickViewParams);
  }

  colorSwatchChanged(newColor: string): void {
    // Only fire if the color changes, otherwise this would fire on hover everytime
    if (this.selectedSwatchColor() !== newColor) {
      this.selectedSwatchColor.set(newColor);
    }
  }

  productCardClicked(event: Event): void {
    this.productCardClick.emit({ event, pdpUrl: this.updatedAssetSeoUrl() });
    if (this.isCriteo()) {
      this.dmnAdService.sendBeacon(this.clickTrackerUrl(), this.beaconQueryParametersClick);
    }
  }

  handleCompareSelection(isChecked: boolean): void {
    const compareData: CompareBarProduct = {
      imgURL: this.productCardImage()?.imageSrc ?? '',
      ecode: this.ecode(),
      name: this.title(),
      pdpURL: this.updatedAssetSeoUrl(),
    };
    if (this.isCriteo()) {
      this.dmnAdService.sendBeacon(this.clickTrackerUrl(), this.beaconQueryParametersClick);
    }
    this.compareCheckboxClick.emit({ compareData, isChecked });
  }

  handleCompareTab(): void {
    if (this.compareBarProductsCount() >= 2) {
      this.showSkipToComparisonBarButton.set(true);
    }
  }

  skipToComparisonBar(): void {
    const isDesktop = this.deviceService.getDeviceType() === 'desktop';
    const selector = isDesktop
      ? '#sticky-filter-container-desktop .plp-srlp-compare-bar button'
      : '#sticky-filter-container-mobile .plp-srlp-compare-bar button';
    this.window.document.querySelector<HTMLButtonElement>(selector)?.focus();
  }

  private addQueryParam(url: string, param: string, value: string): string {
    const connector = url.includes('?') ? '&' : '?';
    return `${url}${connector}${param}=${value}`;
  }

  trackByFn(index: number, _item: unknown): number {
    return index;
  }

  public ecodeSelectedEmitter(): void {
    this.ecodeSelected.emit(this.ecode());
  }

  private buildProductCardAriaLabel(): string {
    let productCardAriaLabel = `${this.title()}`;

    const productCardAriaLabelDescriptions = {
      pricing: this.getPricingString() || '',
      reviewStars: this.getReviewStarsAriaLabel() || '',
      promo: this.promo() || '',
      fulfillment: this.getFulfillmentAriaLabel() || '',
    };

    Object.values(productCardAriaLabelDescriptions).forEach((description) => {
      if (description) {
        productCardAriaLabel += `, ${description}`;
      }
    });

    return productCardAriaLabel;
  }

  private getPricingString(): string {
    let newPriceString = '';
    const prices = this.prices();

    // Full Price - no Price In Cart (PiC) nor previous or 'was' price (WAS)
    if (this.priceIndicator() === 1 && this.mapPriceIndicator() === 0) {
      newPriceString = `$${prices.minOfferPrice}`;

      if (prices.minOfferPrice !== prices.maxOfferPrice) {
        newPriceString += ` to $${prices.maxOfferPrice}`;
      }
    }

    // Sale Price - WAS price present, no PiC
    if (this.priceIndicator() === 0 && this.mapPriceIndicator() === 0) {
      let lowerPrice = `New Lower Price: $${prices.minOfferPrice}`;

      if (prices.minOfferPrice !== prices.maxOfferPrice) {
        lowerPrice += ` to $${prices.maxOfferPrice}`;
      }

      let wasPrice = `Previous Price: $${prices.minListPrice}`;

      if (prices.minListPrice !== prices.maxListPrice) {
        wasPrice += ` to $${prices.maxListPrice}`;
      }

      newPriceString = `${lowerPrice} , ${wasPrice}`;
    }

    // PiC and WAS
    if (this.mapPriceIndicator() === 1) {
      newPriceString = `See Price in Cart. Previous Price: $${prices.minListPrice}`;

      if (prices.minListPrice !== prices.maxListPrice) {
        newPriceString += ` to $${prices.maxListPrice}`;
      }
    }

    return newPriceString;
  }

  private getReviewStarsAriaLabel(): string {
    if (!this.showCustomerRating() || !this.ratingCount()) {
      return '';
    }

    // ratingValue % 1 !== 0 tells if the number has decimals or not
    return `${this.ratingValueDisplay()} out of 5 stars based on ${this.ratingCount()} reviews`;
  }

  private getFulfillmentAriaLabel(): string {
    let fulfillmentAriaLabel = '';
    let shippingMessage = '';
    let pickupMessage = '';
    if (this.productAvailabilityData()) {
      const availability = this.productAvailabilityData() as ProductAvailability;
      shippingMessage = SHIPPING_MESSAGES[availability?.onlineInventoryMessage]?.text ?? '';
      pickupMessage = availability?.inStoreInventoryMessage
        ? (PICKUP_MESSAGES[availability?.inStoreInventoryMessage]?.text ?? '')
        : '';
    }

    if (shippingMessage) {
      fulfillmentAriaLabel += shippingMessage;
    }

    if (shippingMessage && pickupMessage) {
      fulfillmentAriaLabel += ', ';
    }

    if (pickupMessage) {
      fulfillmentAriaLabel += `${pickupMessage}`;
    }

    return fulfillmentAriaLabel;
  }

  private formatBadge(badgeValue: string | undefined): string {
    if (
      !badgeValue ||
      (this.chainAbbr() !== 'dsg' && badgeValue.toLowerCase() === 'dicksexclusive')
    ) {
      return '';
    }
    return badgeValue;
  }
}
